// -----------------------------------------------------------------------
// Usage tracker — client half of Phase 1 analytics.
//
// v40c admin-tracking-detail upgrade:
//   * Routes split: /exchange-mapping now reports `exchange_mapping`
//     (its own section) instead of being lumped under `gap_matrix`.
//     /unaffiliated/:channelId reports `unaffiliated_profile`.
//     /search?tab=unaffiliated reports `creator_intel_unaffiliated` —
//     the tracker now inspects window.location.search as well as
//     pathname so the Creator Intel sub-tab doesn't get flattened to
//     "creator_intel" alongside the Affiliated tab.
//   * Idle segments: on every real activity bump the tracker checks
//     the gap since the last activity. Pauses up to IDLE_THRESHOLD_MS
//     are counted as reading (not idle). Longer pauses emit an
//     `idle_segment` event with the exact start_ts and duration_ms so
//     the admin timeline can show the idle stretch where it happened.
//     On page_leave / tab_hide / session_end any still-open idle gets
//     flushed up to the leave moment. Active time per page is derived
//     server-side as (duration_ms − Σ idle_segments inside).
//   * trackPageView no longer early-returns when the new section is
//     unknown — it still emits a page_leave for the previous page so
//     unknown routes don't inflate the previous page's time.
//
// Round 11 (v32) baseline — "to the second, live":
//   * Session boundaries: fires `session_start` on tab load (new tab_id)
//     and `session_end` on `pagehide` / unload (sendBeacon so it survives).
//   * Precise page durations: fires `page_leave` with `duration_ms`
//     whenever the route changes OR the tab hides OR the user navigates
//     away. The matching `page_view` carries `enter_ts`. Backend uses
//     these two events to time pages to the second.
//   * Visibility: `tab_hide` + `tab_show` with the exact ts.
//   * Login / logout markers for the user-list drill-down timeline.
//   * Filter / view selection tracking: `trackFilter(section, key, value)`.
//   * No IP, no device fingerprint, no raw UA.
//
// Design contract with the product owner:
//   * Recording must NEVER slow the app down — every send is batched,
//     async, and off the render path.
//   * Only fires when the tab is VISIBLE and there has been real activity
//     within the last 2 minutes (otherwise a background tab left open
//     inflates active time to ~8h). Idle detection has its own cadence
//     via IDLE_THRESHOLD_MS which must be smaller than ACTIVE_WINDOW_MS.
//   * No text capture. No session replay.
// -----------------------------------------------------------------------

import { api } from "@/lib/api";

const HEARTBEAT_MS = 30_000;
const ACTIVE_WINDOW_MS = 2 * 60_000;
const FLUSH_MS = 10_000;              // flush slightly faster so admin screens see live data within ~10s
const MAX_QUEUE = 400;
// v40c — pauses up to 20 seconds between two activities count as
// reading (active). Anything longer is an idle segment. Chosen so a
// reader scanning a creator profile isn't flagged idle every scroll.
const IDLE_THRESHOLD_MS = 20_000;

// Section-key mapping — mirrors USAGE_SECTION_ALLOWED on the backend.
// Order matters: more specific prefixes first. v40c added the
// exchange_mapping split and the unaffiliated_profile route.
const ROUTE_TO_SECTION = [
  { re: /^\/search\b/, section: "creator_intel" }, // sub-split by ?tab= below
  // v42b — the My Radar creator page is its own section so time on a
  // creator page is tracked separately from the list. The id is
  // stripped from the path below (see `routeToSection`) so events
  // never carry it.
  { re: /^\/my-radar\/workspace\b/, section: "my_radar_workspace" },
  { re: /^\/my-radar\/insights\b/, section: "my_radar_insights" },
  { re: /^\/my-radar\/creator\b/, section: "my_radar_profile" },
  { re: /^\/my-radar/, section: "my_radar" },
  // v43 — X Creators (preview only). Separate sections for the list and
  // the profile. The handle is stripped from the path before emitting
  // (see stripIdFromPath) so events never carry it.
  { re: /^\/x-creators\/[^/]+/, section: "x_creators_profile" },
  { re: /^\/x-creators/, section: "x_creators" },
  { re: /^\/exchange-mapping/, section: "exchange_mapping" },
  { re: /^\/gap-matrix/, section: "gap_matrix" },
  { re: /^\/prospect-radar/, section: "prospect_radar" },
  { re: /^\/bot-detection/, section: "bot_detection" },
  { re: /^\/live-streaming-intel\/[^/]+/, section: "live_profile" },
  { re: /^\/live-streaming-intel/, section: "live_intel" },
  { re: /^\/alerts/, section: "alerts" },
  { re: /^\/compare(?!.*exchange)/, section: "compare" },
  { re: /^\/exchange-compare/, section: "compare" },
  { re: /^\/exchange\//, section: "exchange_detail" },
  { re: /^\/roi-calculator/, section: "roi_calculator" },
  { re: /^\/lists/, section: "lists" },
  { re: /^\/kol\//, section: "kol_profile" },
  { re: /^\/unaffiliated\//, section: "unaffiliated_profile" },
  { re: /^\/admin\/hidden/, section: "hidden_creators" },
  { re: /^\/admin/, section: "admin" },
  { re: /^\/(privacy|terms|disclaimer)/, section: "legal" },
  { re: /^\/$/, section: "landing" },
];

const state = {
  queue: [],
  lastActivityTs: Date.now(),
  visible: typeof document !== "undefined" ? !document.hidden : true,
  heartbeatTimer: null,
  flushTimer: null,
  started: false,
  sessionHint: null,
  lastPageSection: null,
  pageEnterTs: null,
  pagePath: null,
  pageEntityType: null,
  pageEntityId: null,
  pageEntityLabel: null,
  endFired: false,
  // v40c idle tracking — see IDLE_THRESHOLD_MS. `idleOpenStartTs` is
  // non-null when we're currently inside an idle segment (will be
  // closed either by the next activity bump or by page_leave /
  // tab_hide / session_end).
  idleOpenStartTs: null,
};

function makeSessionHint() {
  return Math.random().toString(36).slice(2, 14);
}

function routeToSection(fullPath) {
  // fullPath may include "?tab=..." — strip the query for the regex
  // match but inspect it for sub-section splits.
  const pathOnly = (fullPath || "").split("?")[0] || "/";
  for (const rule of ROUTE_TO_SECTION) {
    if (rule.re.test(pathOnly)) {
      // v40c — /search?tab=unaffiliated splits creator_intel.
      if (rule.section === "creator_intel") {
        try {
          const q = (fullPath || "").split("?")[1] || "";
          const params = new URLSearchParams(q);
          if ((params.get("tab") || "").toLowerCase() === "unaffiliated") {
            return "creator_intel_unaffiliated";
          }
          return "creator_intel_affiliated";
        } catch { /* fall through */ }
      }
      return rule.section;
    }
  }
  return null;
}

// v42b — strip the channel id from the /my-radar/creator path so
// usage events on that page never carry the creator id (brief 6).
function stripIdFromPath(pathOnly, section) {
  if (section === "my_radar_profile") return "/my-radar/creator";
  // v43 — same rule for X Creator profiles. The handle is never
  // emitted; the client reports /x-creators/profile instead.
  if (section === "x_creators_profile") return "/x-creators/profile";
  return pathOnly;
}

// v48 — where a creator profile was opened from. Only a fixed key is
// kept (never a query string or anything typed).
const PROFILE_SECTIONS = new Set(["kol_profile", "unaffiliated_profile", "x_creators_profile", "live_profile"]);
function sourceKey(fullPath) {
  if (!fullPath) return "direct";
  const [pathOnly, q = ""] = fullPath.split("?");
  const params = new URLSearchParams(q);
  if (/^\/search\b/.test(pathOnly)) {
    if ((params.get("tab") || "").toLowerCase() === "unaffiliated") {
      const sub = (params.get("sub") || "").toLowerCase();
      if (sub === "moves") return "market_moves";
      if (sub === "alerts") return "unaffiliated_alerts";
      return "unaffiliated_list";
    }
    return params.get("q") ? "search" : "creator_intel";
  }
  if (/^\/(kol|unaffiliated)\//.test(pathOnly) || /^\/x-creators\/[^/]+/.test(pathOnly)) return "creator_page";
  if (/^\/my-radar/.test(pathOnly)) return "my_radar";
  if (/^\/lists/.test(pathOnly)) return "lists";
  if (/^\/exchange-compare/.test(pathOnly)) return "compare";
  const sec = routeToSection(pathOnly);
  return sec || "other";
}

function isActive() {
  return state.visible && (Date.now() - state.lastActivityTs) < ACTIVE_WINDOW_MS;
}

function enqueue(ev) {
  if (state.queue.length >= MAX_QUEUE) {
    state.queue.shift();
  }
  const nowMs = Date.now();
  if (nowMs !== state._lastTickMs) {
    state._lastTickMs = nowMs;
    state._tickSeq = 0;
  } else {
    state._tickSeq = (state._tickSeq || 0) + 1;
  }
  const microOffset = Math.min(999, state._tickSeq);
  const d = new Date(nowMs);
  const ts = d.toISOString().replace(/\.(\d{3})Z$/, (_, ms) => `.${ms}${String(microOffset).padStart(3, "0")}Z`);
  state.queue.push({ ...ev, client_ts: ts, session_hint: state.sessionHint });
}

async function flush() {
  if (state.queue.length === 0) return;
  const batch = state.queue.splice(0, MAX_QUEUE);
  try {
    await api.post("/analytics/track", { events: batch });
  } catch {
    // best-effort; drop on failure
  }
}

function beaconFlush() {
  if (state.queue.length === 0) return;
  const batch = state.queue.splice(0, MAX_QUEUE);
  const url = api.defaults.baseURL + "/analytics/track";
  const body = JSON.stringify({ events: batch });
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
  } catch { /* fall through */ }
  try { fetch(url, { method: "POST", body, credentials: "include", headers: { "Content-Type": "application/json" }, keepalive: true }); } catch { /* ignore */ }
}

// v40c — close any open idle segment at the given moment and emit it.
// `endMs` defaults to now. The segment runs from `idleOpenStartTs`
// (which is `lastActivityTs + IDLE_THRESHOLD_MS`) to endMs, so pauses
// up to the threshold count as reading, longer pauses emit the gap.
function flushIdleSegment(endMs) {
  if (!state.idleOpenStartTs) return;
  const end = typeof endMs === "number" ? endMs : Date.now();
  const dur = Math.max(0, end - state.idleOpenStartTs);
  if (dur > 0 && state.lastPageSection) {
    const startIso = new Date(state.idleOpenStartTs).toISOString();
    enqueue({
      kind: "idle_segment",
      section: state.lastPageSection,
      path: state.pagePath,
      idle_start_ts: startIso,
      duration_ms: dur,
    });
  }
  state.idleOpenStartTs = null;
}

function emitPageLeave(reason) {
  if (state.pageEnterTs == null || !state.lastPageSection) return;
  // v40c — close any still-open idle segment at the leave moment so
  // the server knows idle extended right up to the page swap.
  flushIdleSegment(Date.now());
  const dur = Math.max(0, Date.now() - state.pageEnterTs);
  enqueue({
    kind: "page_leave",
    section: state.lastPageSection,
    path: state.pagePath,
    duration_ms: dur,
    reason: reason || null,
    entity_type: state.pageEntityType,
    entity_id: state.pageEntityId,
    entity_label: state.pageEntityLabel,
  });
  state.pageEnterTs = null;
}

// -------- Public API --------------------------------------------------

export function startTracker() {
  if (state.started || typeof window === "undefined") return;
  state.started = true;
  state.sessionHint = makeSessionHint();

  enqueue({ kind: "session_start" });

  // v40c — on every activity bump: if the gap since last activity
  // exceeded the reading threshold, retroactively close the idle
  // segment that was running during the gap. Then update lastActivityTs.
  const bumpActivity = () => {
    const now = Date.now();
    const gap = now - state.lastActivityTs;
    if (gap > IDLE_THRESHOLD_MS && state.lastPageSection) {
      // Idle segment starts AFTER the reading grace, ends at now.
      if (!state.idleOpenStartTs) {
        state.idleOpenStartTs = state.lastActivityTs + IDLE_THRESHOLD_MS;
      }
      flushIdleSegment(now);
    }
    state.lastActivityTs = now;
  };
  ["mousemove", "keydown", "click", "scroll", "touchstart"].forEach((ev) => {
    window.addEventListener(ev, bumpActivity, { passive: true });
  });
  document.addEventListener("visibilitychange", () => {
    state.visible = !document.hidden;
    if (document.hidden) {
      enqueue({ kind: "tab_hide", section: state.lastPageSection });
      emitPageLeave("tab_hide");
      beaconFlush();
    } else {
      enqueue({ kind: "tab_show", section: state.lastPageSection });
      state.pageEnterTs = Date.now();
      state.lastActivityTs = Date.now();
      state.idleOpenStartTs = null;
    }
  });
  const endOnce = () => {
    if (state.endFired) return;
    state.endFired = true;
    emitPageLeave("session_end");
    enqueue({ kind: "session_end" });
    beaconFlush();
  };
  window.addEventListener("pagehide", endOnce);
  window.addEventListener("beforeunload", endOnce);

  // Heartbeat — ticks every 30s while the tab is visible AND there has
  // been activity within the last 2 minutes. v40c additionally opens an
  // idle segment if the heartbeat finds us past the reading threshold
  // (so a user sitting on a page quietly still accumulates idle in
  // real time, not only when they come back).
  state.heartbeatTimer = setInterval(() => {
    const now = Date.now();
    const gap = now - state.lastActivityTs;
    if (gap > IDLE_THRESHOLD_MS && state.lastPageSection && !state.idleOpenStartTs) {
      state.idleOpenStartTs = state.lastActivityTs + IDLE_THRESHOLD_MS;
    }
    if (!isActive()) return;
    const section = routeToSection(window.location.pathname + (window.location.search || ""));
    enqueue({ kind: "heartbeat", section });
  }, HEARTBEAT_MS);

  state.flushTimer = setInterval(flush, FLUSH_MS);
}

export function stopTracker() {
  if (state.heartbeatTimer) clearInterval(state.heartbeatTimer);
  if (state.flushTimer) clearInterval(state.flushTimer);
  state.heartbeatTimer = null;
  state.flushTimer = null;
  state.started = false;
}

// Route-change page view. v40c: still emits a page_leave for the
// previous page even when the new path has no mapped section (so
// unknown routes don't inflate the previous page's time). Accepts
// pathname + search ("/search?tab=unaffiliated") so Creator Intel's
// sub-tabs are split without needing an explicit override.
export function trackPageView(pathname) {
  const full = pathname || (typeof window !== "undefined"
    ? window.location.pathname + (window.location.search || "")
    : "");
  const section = routeToSection(full);
  const pathOnly = full.split("?")[0];
  const prevFull = state.lastFullPath;
  state.lastFullPath = full;
  // v40c — leave the previous page UNCONDITIONALLY on a route change.
  // Pre-v40c we returned early when the new section was unknown, which
  // let the previous page's duration bleed into the next one.
  if (state.lastPageSection && state.pageEnterTs != null) {
    // Only emit a leave if the (section, path) actually changed, so a
    // re-render on the same route doesn't double-fire. Compare the
    // stripped path on both sides (v42b).
    const newStripped = stripIdFromPath(pathOnly, section);
    if (state.pagePath !== newStripped || state.lastPageSection !== section) {
      emitPageLeave("route_change");
    } else {
      return;
    }
  }
  if (!section) return;
  state.lastPageSection = section;
  state.pagePath = stripIdFromPath(pathOnly, section);
  state.pageEnterTs = Date.now();
  state.pageEntityType = null;
  state.pageEntityId = null;
  state.pageEntityLabel = null;
  state.idleOpenStartTs = null;
  state.lastActivityTs = Date.now();
  const ev = { kind: "page_view", section, path: state.pagePath };
  if (PROFILE_SECTIONS.has(section)) ev.from_page = sourceKey(prevFull);
  enqueue(ev);
}

// v48a — a creator's contact detail was copied (button or select+copy).
// Only that it happened and which kind — never the copied text.
export function trackContactCopy(channelId, contactKind, section) {
  if (!channelId || !contactKind) return;
  enqueue({ kind: "contact_copy", entity_type: "creator", entity_id: String(channelId), reason: contactKind, ...(section ? { section } : {}) });
}

// v48a — selection copies inside any element marked data-contact-kind.
export function installContactCopyTracking(channelId) {
  if (typeof document === "undefined" || !channelId) return () => {};
  const onCopy = () => {
    const sel = window.getSelection && window.getSelection();
    if (!sel || sel.isCollapsed) return;
    const seen = new Set();
    [sel.anchorNode, sel.focusNode].forEach((n) => {
      const el = n && (n.nodeType === 1 ? n : n.parentElement);
      const host = el && el.closest && el.closest("[data-contact-kind]");
      if (host) seen.add(host.getAttribute("data-contact-kind"));
    });
    seen.forEach((k) => trackContactCopy(channelId, k));
  };
  document.addEventListener("copy", onCopy);
  return () => document.removeEventListener("copy", onCopy);
}

// v48 — a click on a creator's contact link. Only that it was clicked
// and which kind (email / youtube / telegram / x / instagram).
export function trackContactClick(channelId, contactKind) {
  if (!channelId || !contactKind) return;
  enqueue({ kind: "contact_click", entity_type: "creator", entity_id: String(channelId), reason: contactKind });
}

export function trackEntityView(entityType, entityId, entityLabel, section) {
  if (!entityType || !entityId) return;
  const sec = section || routeToSection(window.location.pathname + (window.location.search || ""));
  const idStr = String(entityId);
  const labelStr = entityLabel ? String(entityLabel).slice(0, 200) : null;
  state.pageEntityType = entityType;
  state.pageEntityId = idStr;
  state.pageEntityLabel = labelStr;
  enqueue({
    kind: "entity_view",
    section: sec,
    entity_type: entityType,
    entity_id: idStr,
    entity_label: labelStr,
  });
}

export function trackFeature(section) {
  if (!section) return;
  enqueue({ kind: "feature_view", section });
}

// v41d — fire-and-flush marker for the quiet-refresh watcher so the
// admin drill-down timeline records WHY a session suddenly ended +
// why a new one started 50ms later ("Reloaded for new build" instead
// of a mystery session boundary). Uses sendBeacon directly because
// the page is about to tear down.
export function trackVersionReload(from, to) {
  try {
    const ev = {
      kind: "version_reload",
      section: "system",
      filter_key: "build_from_to",
      filter_value: `${from || "?"}→${to || "?"}`,
      client_ts: new Date().toISOString(),
      session_hint: state.sessionHint,
    };
    const url = api.defaults.baseURL + "/analytics/track";
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify({ events: [ev] })], { type: "application/json" });
      navigator.sendBeacon(url, blob);
    }
  } catch (_) {}
}

// Filter / view selection — fires when the user picks a view chip, tier
// filter, universe chip, or changes a grid sort. v40c expands coverage
// across GapMatrix, ExchangeMapping, and UnaffiliatedGrid (callers in
// those pages pass the right section).
export function trackFilter(section, key, value) {
  if (!key) return;
  enqueue({
    kind: "filter_use",
    section: section || routeToSection(window.location.pathname + (window.location.search || "")),
    filter_key: String(key).slice(0, 48),
    filter_value: value != null ? String(value).slice(0, 96) : null,
  });
}

export function trackLogin() {
  enqueue({ kind: "login" });
}
export function trackLogout() {
  enqueue({ kind: "logout" });
  flush();
}

// -------- Unmet search — separate endpoint --------------------------------
const _reportedUnmet = new Set();

export async function trackUnmetSearch({ fingerprint, label, filters }) {
  if (!fingerprint || _reportedUnmet.has(fingerprint)) return;
  _reportedUnmet.add(fingerprint);
  try {
    await api.post("/analytics/unmet-search", { fingerprint, label, filters });
  } catch {
    _reportedUnmet.delete(fingerprint);
  }
}
